import style from "./SettingsForm.module.css";

import { useFormWithValidation } from "@/features/forms/hooks/useFormWithValidation";
import { useEditSettings } from "../../hooks/useEditSettings";

import { FormProvider, useFormContext } from "react-hook-form";
import FormInput from "@/features/forms/components/FormInput";
import FormImage from "@/features/forms/components/FormImage";
import InputPhone from "@/features/forms/components/InputPhone";

import SettingsSection from "../settingsSection/SettingsSection";

import { successToast } from "@/ui/toast/Toast";
import Button from "@/ui/button/Button";
import Icon from "@/ui/icon/Icon";
import { useEffect, useMemo } from "react";
import i18n from "@/i18n";
import FormDropdown from "@/features/forms/components/FormDropdown";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/ui/languageSwitcher/LanguageSwitcher";
import { useLanguage } from "@/i18n/useLanguage";

// Certificate language codes, as the API stores them, with the catalogue key of
// each one's name. Keys rather than text, so the list follows the app's language.
const CERTIFICATE_LANGUAGES = [
	{ value: "BA", labelKey: "settings:certificate.languages.bosnian" },
	{ value: "HR", labelKey: "settings:certificate.languages.croatian" },
	{ value: "SR", labelKey: "settings:certificate.languages.serbian" },
	{ value: "EN", labelKey: "settings:certificate.languages.english" },
	{ value: "DE", labelKey: "settings:certificate.languages.german" },
];

export default function SettingsForm({ user }) {
	const { t } = useTranslation();
	const { enabled: languageSwitchEnabled } = useLanguage();

	const initialCertificateLanguage = ["BA", "HR", "SR", "EN", "DE"].includes(
		user.certificate_language
	)
		? user.certificate_language
		: "BA";

	const formMethods = useFormWithValidation({
		values: {
			username: user.username,
			email: user.email,
			first_name: user.firstName,
			last_name: user.lastName,
			phone_number: user.phoneNumber || "",
			picture: user.safe_picture_url,
			certificate_language: initialCertificateLanguage,
		},
	});

	const {
		handleSubmit,
		formState: { isSubmitting, isDirty },
		watch,
	} = formMethods;

	const { save } = useEditSettings();

	const onSubmit = async (data) => {
		await save(data);
		successToast(i18n.t("settings:saved"));
	};

	const watchedValues = watch();

	useEffect(() => {
		if (watchedValues.picture && isDirty) {
			onSubmit(watchedValues);
		}
	}, [watchedValues.picture]);

	useEffect(() => {
		if (watchedValues.picture_clear && isDirty) {
			onSubmit(watchedValues);
		}
	}, [watchedValues.picture_clear]);

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<SettingsSection
					title={t("settings:profile.title")}
					description={t("settings:profile.description")}
				>
					<div
						className={style.userCard}
						style={{
							"--background": `url(${watchedValues.picture})`,
						}}
					>
						<div className={style.imgHolder}>
							<FormImage
								initialPreviewImageURL={user.picture}
								name="picture"
							/>
						</div>
						<div className={style.info}>
							<span className={style.username}>
								@{watchedValues.username}
							</span>
							<span className={style.fullName}>
								{watchedValues.first_name}{" "}
								{watchedValues.last_name}
							</span>
							<span className={style.phoneNumber}>
								<Icon icon="fa-phone" />
								<span>{watchedValues.phone_number}</span>
							</span>
						</div>
					</div>
				</SettingsSection>

				<SettingsSection
					title={t("common:fields.email.label")}
					description={t("settings:email.description")}
				>
					<FormInput
						notAllowed
						name="email"
						label={t("settings:email.label")}
					/>
				</SettingsSection>

				<SettingsSection
					title={t("settings:personal.title")}
					description={t("settings:personal.description")}
				>
					<FormInput
						name="username"
						label={t("common:fields.username.label")}
					/>
					<FormInput
						name="first_name"
						label={t("common:fields.firstName.label")}
					/>
					<FormInput
						name="last_name"
						label={t("common:fields.lastName.label")}
					/>
					<InputPhone
						name="phone_number"
						label={t("common:fields.phone.label")}
					/>
				</SettingsSection>

				{/* Applies and saves on click, not with "Sačuvaj": the whole
				    form re-renders in the new language. Its buttons are
				    type="button", so they never submit the form. */}
				{languageSwitchEnabled && (
					<SettingsSection
						title={t("settings:language.title")}
						description={t("settings:language.description")}
					>
						<LanguageSwitcher variant="options" />
					</SettingsSection>
				)}

				<SettingsSection
					title={t("settings:certificate.title")}
					description={t("settings:certificate.description")}
				>
					<FormDropdown
						name="certificate_language"
						label={t("settings:certificate.title")}
						placeholder={t("settings:certificate.placeholder")}
						options={CERTIFICATE_LANGUAGES.map((option) => ({
							value: option.value,
							label: t(option.labelKey),
						}))}
					/>
				</SettingsSection>

				<div className={style.btnHolder}>
					<Button
						className={style.btn}
						type="accent"
						disabled={isSubmitting}
					>
						<Icon icon="fa-floppy-disk" />
						{isSubmitting ? (
							<Icon icon="fa-ellipsis" />
						) : (
							t("common:actions.save")
						)}
					</Button>
				</div>
			</form>
		</FormProvider>
	);
}
